import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  getZktPunches,
  upsertManualAttendanceStatus,
} from "../../services/kmtAttendance.service";
import "bootstrap/dist/css/bootstrap.rtl.min.css";
import "./FlexibleManualAttendance.css";

const STATUS_OPTIONS = [
  {
    value: "PRESENT",
    label: "حضور",
    hint: "الوضع الافتراضي — يوم حضور بدون بصمة",
  },
  {
    value: "ABSENT",
    label: "غياب",
    hint: "يُخصم من الراتب كغياب غير مصرّح (مصدر HR)",
  },
  {
    value: "LATE",
    label: "تأخير",
    hint: "تسجيل تأخير يدوي بدون جزاء تأخير تلقائي",
  },
  {
    value: "HALF_DAY",
    label: "نصف يوم",
    hint: "حضور جزئي",
  },
  {
    value: "REST",
    label: "إجازة / راحة",
    hint: "إجازة أو راحة أسبوعية — بدون خصم غياب",
  },
];

const STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function employeeLabel(emp) {
  const name = emp?.name || emp?.fullName || "بدون اسم";
  const code = emp?.code || emp?.employeeCode || "";
  return code ? `${name} (${code})` : name;
}

function todayIsoDate() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toIsoDate(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const y = parsed.getUTCFullYear();
  const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const d = String(parsed.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(value) {
  const raw = toIsoDate(value);
  if (!raw) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
}

function sourceLabel(row) {
  if (row?.implicit) return "افتراضي";
  if (row?.source === "HR") return "يدوي HR";
  if (row?.source === "TIMESHEET") return "خطة";
  return row?.source || "—";
}

export default function FlexibleManualAttendance() {
  const { employeesByBranch } = useData();
  const formCardRef = useRef(null);
  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState(todayIsoDate);
  const [status, setStatus] = useState("PRESENT");
  const [notes, setNotes] = useState("");
  const [editingDate, setEditingDate] = useState("");
  const [formFlash, setFormFlash] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);

  const flexibleEmployees = useMemo(() => {
    return Object.values(employeesByBranch || {})
      .flat()
      .filter(
        (e) =>
          e &&
          e.id &&
          e.status !== "terminated" &&
          Boolean(e.manualAttendanceOnly),
      )
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "ar"),
      );
  }, [employeesByBranch]);

  const selectedStatus = STATUS_OPTIONS.find((o) => o.value === status);

  const loadDays = useCallback(async () => {
    if (!employeeId) {
      setDays([]);
      return;
    }
    setLoadingDays(true);
    setError("");
    try {
      const data = await getZktPunches(month, year, { employeeId });
      const list = Array.isArray(data?.days) ? data.days : [];
      setDays(list);
    } catch (err) {
      setDays([]);
      setError(getApiErrorMessage(err, "تعذر تحميل سجلات الشهر"));
    } finally {
      setLoadingDays(false);
    }
  }, [employeeId, month, year]);

  useEffect(() => {
    loadDays();
  }, [loadDays]);

  useEffect(() => {
    if (
      employeeId &&
      !flexibleEmployees.some((e) => e.id === employeeId)
    ) {
      setEmployeeId("");
    }
  }, [employeeId, flexibleEmployees]);

  useEffect(() => {
    if (!formFlash) return undefined;
    const timer = window.setTimeout(() => setFormFlash(false), 1400);
    return () => window.clearTimeout(timer);
  }, [formFlash]);

  const displayDays = useMemo(() => {
    const byDate = new Map();
    for (const row of days) {
      const iso = toIsoDate(row.date);
      if (iso) byDate.set(iso, row);
    }

    const today = todayIsoDate();
    const lastDay = new Date(year, month, 0).getDate();
    const rows = [];
    for (let day = 1; day <= lastDay; day += 1) {
      const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const rec = byDate.get(iso);
      if (rec) {
        rows.push({
          ...rec,
          date: iso,
          implicit: false,
          future: iso > today,
        });
        continue;
      }
      const future = iso > today;
      rows.push({
        id: `implicit-${iso}`,
        date: iso,
        status: future ? null : "PRESENT",
        source: null,
        lateMinutes: 0,
        implicit: true,
        future,
      });
    }
    return rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [days, month, year]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!employeeId) {
      setError("اختر موظفاً مرناً");
      return;
    }
    if (!date) {
      setError("اختر التاريخ");
      return;
    }

    setSubmitting(true);
    try {
      await upsertManualAttendanceStatus({
        employeeId,
        date,
        status,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      const emp = flexibleEmployees.find((x) => x.id === employeeId);
      setSuccess(
        `تم تسجيل «${STATUS_LABELS[status] || status}» لـ ${employeeLabel(emp)} يوم ${formatDisplayDate(date)}`,
      );
      setNotes("");
      setEditingDate(date);
      const recordMonth = Number(String(date).slice(5, 7));
      const recordYear = Number(String(date).slice(0, 4));
      if (recordMonth === month && recordYear === year) {
        await loadDays();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ التسجيل اليدوي"));
    } finally {
      setSubmitting(false);
    }
  }

  function fillFromRow(row) {
    if (!row) return;
    const nextDate = toIsoDate(row.date);
    if (!nextDate) {
      setError("تعذر قراءة تاريخ هذا اليوم للتعديل");
      return;
    }
    setDate(nextDate);
    setStatus(STATUS_LABELS[row.status] ? row.status : "PRESENT");
    setNotes("");
    setEditingDate(nextDate);
    setFormFlash(true);
    setError("");
    setSuccess("");
    window.requestAnimationFrame(() => {
      formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  const isEditing = Boolean(editingDate) && editingDate === date;

  return (
    <div className="flex-manual-page" dir="rtl">
      <div className="ess-home-header mb-3">
        <h1 className="ess-home-title">حضور يدوي — ساعات مرنة</h1>
        <p className="ess-home-subtitle mb-0">
          الموظف حضور بشكل افتراضي. سجّل يدوياً فقط عند الغياب أو الإجازة أو أي
          إجراء آخر. لا يُحسب غياب تلقائي؛ الخصم من الراتب يعتمد على تسجيل الغياب هنا.
        </p>
      </div>

      {flexibleEmployees.length === 0 ? (
        <div className="alert alert-light border">
          لا يوجد موظفون بساعات مرنة حالياً. فعّلهم أولاً من{" "}
          <strong>نظام الحضور</strong> (عمود ساعات مرنة).
        </div>
      ) : null}

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      <div
        className={`card flex-manual-card mb-3${isEditing ? " flex-manual-card--editing" : ""}${formFlash ? " flex-manual-card--flash" : ""}`}
        ref={formCardRef}
        id="flex-manual-form"
      >
        <div className="card-body">
          <h2 className="h5 mb-3">
            {isEditing
              ? `تعديل يوم ${formatDisplayDate(date)}`
              : "تسجيل يوم"}
          </h2>
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label">الموظف *</label>
              <select
                className="form-select"
                value={employeeId}
                onChange={(e) => {
                  setEmployeeId(e.target.value);
                  setEditingDate("");
                }}
                required
              >
                <option value="">— اختر موظفاً مرناً —</option>
                {flexibleEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {employeeLabel(emp)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">التاريخ *</label>
              <input
                type="date"
                className="form-control"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setEditingDate(e.target.value);
                }}
                required
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">الحالة *</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12">
              <p className="form-text mb-0">{selectedStatus?.hint}</p>
            </div>

            <div className="col-12">
              <label className="form-label">ملاحظة (اختياري)</label>
              <textarea
                className="form-control"
                rows={2}
                maxLength={1000}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="مثال: غياب بدون إذن — تسجيل يدوي"
              />
            </div>

            <div className="col-12">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || flexibleEmployees.length === 0}
              >
                {submitting
                  ? "جاري الحفظ..."
                  : isEditing
                    ? "حفظ التعديل"
                    : "حفظ التسجيل"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card flex-manual-card">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div>
              <h2 className="h5 mb-0">سجلات الشهر</h2>
              <p className="text-muted small mb-0">
                الأيام بدون إجراء من HR تظهر حضور افتراضي
              </p>
            </div>
            <div className="ms-auto d-flex flex-wrap gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(
                  (y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ),
                )}
              </select>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={loadDays}
                disabled={!employeeId || loadingDays}
              >
                تحديث
              </button>
            </div>
          </div>

          {!employeeId ? (
            <div className="text-muted">اختر موظفاً لعرض سجلاته.</div>
          ) : loadingDays ? (
            <div className="text-muted">جاري التحميل...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>الحالة</th>
                    <th>المصدر</th>
                    <th>تأخير (د)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {displayDays.map((row) => {
                    const rowDate = toIsoDate(row.date);
                    const selected = editingDate && editingDate === rowDate;
                    return (
                      <tr
                        key={row.id || rowDate}
                        className={selected ? "flex-manual-row-editing" : undefined}
                      >
                        <td>{formatDisplayDate(row.date)}</td>
                        <td>
                          {row.status ? (
                            <span
                              className={`badge flex-status-badge flex-status-${String(row.status || "").toLowerCase()}`}
                            >
                              {STATUS_LABELS[row.status] || row.status}
                              {row.implicit && row.status === "PRESENT"
                                ? " (افتراضي)"
                                : ""}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>{sourceLabel(row)}</td>
                        <td>{row.lateMinutes ?? 0}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => fillFromRow(row)}
                          >
                            تعديل
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
